import { createEventDefinition, EventBus as TsBus } from "ts-bus";
import type { EntityNames, KnownErrorCodes } from "../config";
import type ChargeResponse from "../entities/chargeResponse";
import type ChargeOrderSearch from "../entities/chargeOrderSearchResponse";
import type RejectedCustomerRefundCandidateDetailResponse from "../entities/rejectedCustomerRefundCandidateDetailResponse";
import type RejectedCustomerRefundCandidateResponse from "../entities/rejectedCustomerRefundCandidateResponse";
import type ExemptionResponse from "../entities/exemptionResponse";
import type Invitation from "../entities/invitation";
import type { IApprovable, IBankAccount } from "../entities/types";
import type UnpairedPayment from "../entities/unpairedPayment";
import type User from "../entities/user";
import type { Notification } from "./notificationService";
import type VehicleChange from "../entities/vehicleChange";
import type CustomerRefundCandidateResponse from "../entities/customerRefundCandidateResponse";
import type CustomerRefundCandidateDetailResponse from "../entities/customerRefundCandidateDetailResponse";
import type TransferPaymentType from "entities/transferPaymentType";
import type LustrationReportRequestCreateResponse from "../entities/lustrationReportRequestCreateResponse";
import type CreateAdHocReportRequestResponse from "entities/createAdHocReportRequestResponse";
import type CreateExemptionRequest from "entities/createExemptionRequest";
import type ExemptionChangeRequest from "entities/exemptionChangeRequest";
import type { ExemptionCancellationRequest } from "entities/exemptionCancellationRequest";
import type InternalReportType from "entities/internalReportType";
import type { ChargeVehicleChangeRequest } from "entities/chargeVehicleChangeRequest";
import type VehicleRegistrationChangeExemptionResponse from "entities/vehicleRegistrationChangeExemptionResponse";
import type VehicleRegistrationChangeChargeResponse from "entities/vehicleRegistrationChangeChargeResponse";
import type VehicleRegisterVehicleSearchStatus from "entities/vehicleRegisterVehicleSearchStatus";
import type CallCenterModelNamesEnum from "entities/callCenterModelNamesEnum";
import type VehicleRegisterVehicle from "entities/vehicleRegisterVehicleResponse";

export type Indexable<TKey extends keyof any, TValue> = { [K in TKey]?: TValue };

export declare type CreatedEvent<TEntity> = {
  type: string;
  payload: Indexable<EntityNames, TEntity>;
  meta?: any;
};

export declare type UpdatedEvent<TEntity> = {
  type: string;
  payload: Indexable<EntityNames, TEntity>;
  meta?: any;
};

export declare type DeletedEvent<TEntity> = {
  type: string;
  payload: Indexable<EntityNames, TEntity>;
  meta?: any;
};

export const localeChanged = createEventDefinition<{ locale: string }>()("locale.changed");
export const serverError = createEventDefinition<{ message?: string; type?: KnownErrorCodes }>()("general.serverError");
export const userLogout = createEventDefinition<void>()("user.logout");
export const notificationAdded = createEventDefinition<{ notification: Notification }>()("notification.added");

export const userUpdated = createEventDefinition<{ user: User }>()("user.updated");
export const invitationExtended = createEventDefinition<{
  user: User;
  invitation: Invitation;
}>()("invitation.extended");

export const exemptionCreated = createEventDefinition<{ entity: CreateExemptionRequest }>()("exemption.created");
export const exemptionUpdated = createEventDefinition<{ entity: ExemptionChangeRequest }>()("exemption.updated");
export const exemptionCancel = createEventDefinition<{ entity: ExemptionCancellationRequest }>()("exemption.cancel");
export const exemptionDelete = createEventDefinition<{ exemptionId: string }>()("exemption.delete");
export const totalCountCharges = createEventDefinition<{ totalCountCharges: number }>()("totalCountCharges.updated");
export const totalCountVehicleRegister = createEventDefinition<{ totalCountVehicleRegister: number }>()(
  "totalCountVehicleRegister.updated"
);
export const internalReportType = createEventDefinition<{ internalReportType: InternalReportType }>()(
  "internalReportType.updated"
);
export const totalCountExemptions = createEventDefinition<{ totalCountExemptions: number }>()("totalCountExemptions.updated");
export const totalCountMessages = createEventDefinition<{ totalCountMessages: number }>()("totalCountMessages.updated");
export const totalCountBankTransfers = createEventDefinition<{ totalCountBankTransfers: number }>()(
  "totalCountBankTransfers.updated"
);
export const totalCountCardGateways = createEventDefinition<{ totalCountCardGateways: number }>()(
  "totalCountCardGateways.updated"
);
export const vehicleSearchStatus = createEventDefinition<{ vehicleSearchStatus: VehicleRegisterVehicleSearchStatus | null }>()(
  "vehicleSearchStatus.updated"
);
export const callCenterViewModelName = createEventDefinition<{ callCenterViewModelName: CallCenterModelNamesEnum }>()(
  "callCenterViewModelName.updated"
);
export const exemptionTerminated = createEventDefinition<{ entity: ExemptionResponse }>()("exemption.terminated");
export const licensePlateChangeChanged = createEventDefinition<{
  entity: ChargeVehicleChangeRequest;
}>()("licensePlateChange.changed");
export const vehiclePlatesChangeChanged = createEventDefinition<{
  entity: VehicleChange;
  charges: VehicleRegistrationChangeChargeResponse[];
  exemptions: VehicleRegistrationChangeExemptionResponse[];
}>()("vehicleLicensePlatesChange.changed");
export const chargeRefundCandidateCreated = createEventDefinition<{
  entity: ChargeResponse;
}>()("charge.refundCandidate.created");
export const chargePartialRefundCandidateCreated = createEventDefinition<{ entity: ChargeResponse }>()(
  "charge.partialRefundCandidate.created"
);
export const approvedChargeRefundCandidatesCreated = createEventDefinition<void>()("charge.refundCandidates.created");
export const chargeFuelTypeChanged = createEventDefinition<{ entity: ChargeResponse }>()("charge.fuelTypeChanged");
export const emailChanged = createEventDefinition<{ entity: ChargeResponse }>()("charge.emailChanged");

export const paymentPaired = createEventDefinition<{
  payment: UnpairedPayment;
  order: ChargeOrderSearch;
}>()("payment.paired");
export const paymentRefunded = createEventDefinition<{ payment: UnpairedPayment }>()("payment.refunded");
export const vehicleSynchronizationOpenDetailRSV = createEventDefinition<{ openDetail: boolean; vehicleId: string }>()(
  "vehicleRegisterSynchronization.openDetail"
);
export const vehicleSynchronizationFetchFlag = createEventDefinition<{ fetchSearch: boolean }>()(
  "vehicleSynchronizationFetchFlag.fetchSearch"
);
export const vehicleSynchronizationOpenSyncRSV = createEventDefinition<{
  openSync: boolean;
  vehicleRegister: VehicleRegisterVehicle;
}>()("vehicleRegisterSynchronization.openSync");

export const allRefundApprovalsApproved = createEventDefinition<void>()("refundApprovals.approved");
export const approvedRefundsExported = createEventDefinition<void>()("refundApprovals.exported");
export const approved = createEventDefinition<{ approvable: IApprovable<any> }>()("approved");
export const distributorRefundGenerated = createEventDefinition<void>()("distributorRefund.generated");
export const distributorPaymentRequestChecked = createEventDefinition<{ paymentRequestId: string }>()(
  "distributorPaymentRequest.checked"
);

export const allApprovedInternalTransfersExported = createEventDefinition<void>()("internalTransfers.exported");

export const allApprovedAccountingDocumentsExported = createEventDefinition<void>()("accountingDocumentCandidate.exported");
export const approvedAccountingDetailsApproved = createEventDefinition()("accountingDocumentCandidate.approved");

export const blacklistedAccountsChanged = createEventDefinition<{
  bankAccount: IBankAccount;
}>()("bankAccount.blacklisted");

export const transferRefundPaymentTypeChanged = createEventDefinition<{
  newTransferType: TransferPaymentType;
  originalTransferType: TransferPaymentType;
  refundId: string;
}>()("transferRefundPayment.refundPaymentTypeChanged");

export const transferRefundForRefundOfRefundPaymentTypeChanged = createEventDefinition<{
  newTransferType: TransferPaymentType;
  originalTransferType: TransferPaymentType;
  refundForRefundOfRefundId: string;
}>()("transferRefundForRefundOfRefundPayment.refundPaymentTypeChanged");

export const customerRefundCandidateRejected = createEventDefinition<{
  customerRefundCandidate: CustomerRefundCandidateResponse | CustomerRefundCandidateDetailResponse;
}>()("customerRefundCandidate.rejected");
export const deadLetterTopicMessagesChanged = createEventDefinition<void>()("deadLetterTopicMessages.changed");
export const customerRefundCandidateExported = createEventDefinition<{
  customerRefundCandidate: CustomerRefundCandidateResponse | CustomerRefundCandidateDetailResponse;
}>()("customerRefundCandidate.exported");
export const rejectedCustomerRefundCandidateRecreated = createEventDefinition<{
  rejectedCustomerRefundCandidate: RejectedCustomerRefundCandidateResponse | RejectedCustomerRefundCandidateDetailResponse;
}>()("rejectedCustomerRefundCandidate.recreated");

// Refunds edit events
export const SEPATransferRefundUpdated = createEventDefinition<void>()("SEPATransferRefund.updated");
export const domesticTransferRefundUpdated = createEventDefinition<void>()("domesticTransferRefund.updated");
export const foreignTransferRefundUpdated = createEventDefinition<void>()("foreignTransferRefund.updated");
export const payGateToTransferRefundUpdated = createEventDefinition<void>()("payGateToTransferRefund.updated");

export const subLedgerReportGenerated = createEventDefinition<void>()("subLedgerReport.generated");

export const lustrationReportFinish = createEventDefinition<{
  lustrationReport: LustrationReportRequestCreateResponse;
}>()("lustrationReport.finish");
export const lustrationReportProcessed = createEventDefinition<{
  lustrationReport: LustrationReportRequestCreateResponse;
}>()("lustrationReport.processed");
export const adhocReportProcessed = createEventDefinition<{
  adhocReport: CreateAdHocReportRequestResponse;
}>()("adhocReport.processed");
export const adhocReportFinish = createEventDefinition<{
  adhocReport: CreateAdHocReportRequestResponse;
}>()("adhocReport.finish");
export default class EventBus extends TsBus {}
